import { ApiUtility } from "../Utilities/ApiUtility.axios";

export enum PracticeDataRequestStatus {
    Unknown = 'Unknown',
    Pending = 'Pending',
    Submitted = 'Submitted',
    Processed = 'Processed'
}

// External = client fills the public form; Internal = our agent fills it in directly.
export enum PracticeDataRequestType {
    External = 1,
    Internal = 2
}

export enum PortalUrlType {
    Subdomain = 1,
    Custom = 2
}

export enum EmailProviderType {
    MicrosoftOutlook = 1,
    Google = 2,
    Other = 3
}

export enum UserCreationMethod {
    UploadCsv = 1,
    CreateViaSCIM = 2
}

export enum LoginMode {
    UserIdPassword = 1,
    SAML = 2
}

export enum CsvTemplateType {
    Unknown = 'Unknown',
    Other = 'Other',
    Contacts = 'Contacts',
    Users = 'Users',
    Receipts = 'Receipts',
    Businesses = 'Businesses',
    CreditNotes = 'CreditNotes',
    Tasks = 'Tasks',
    Subscriptions = 'Subscriptions',
    Invoices = 'Invoices',
    ServicesAndPackages = 'ServicesAndPackages',
    EmailTemplates = 'EmailTemplates',
    ESignatureContent = 'ESignatureContent',
    LoeContent = 'LoeContent',
    PracticeProfileQuestionnaire = 'PracticeProfileQuestionnaire',
    BankAccountsForCollection = 'BankAccountsForCollection',
    Credentials = 'Credentials',
    KycDocumentsList = 'KycDocumentsList',
    AutomationAndEmailReminder = 'AutomationAndEmailReminder',
    TeamsTemplate = 'TeamsTemplate'
}

export interface IFileNameModel {
    id?: string;
    name?: string;
    path?: string;
    contentType?: string;
    length?: number;
}

export interface IValidationError {
    rowNumber: number;
    colName: string;
    value: string;
    message: string;
}

export interface IValidationResult {
    templateType: CsvTemplateType;
    fileName: string;
    totalRows: number;
    validRows: number;
    errorCount: number;
    status: 'Passed' | 'Failed';
    errors: IValidationError[];
}

export interface IEmailTemplateTypeItem {
    key: string;
    section: string;
    partition: string;
    template: string;
}

export enum EmailTemplateSubType {
    Unknown = 'Unknown',
    ContactQuery = 'ContactQuery',
    CallbackRequest = 'CallbackRequest',
    InternalCallbackRequest = 'InternalCallbackRequest',
    QuickQuote = 'QuickQuote',
    QuoteViewed = 'QuoteViewed',
    QuoteAccepted = 'QuoteAccepted',
    ServiceRequest = 'ServiceRequest',
    Referral = 'Referral',
    SinglePayment = 'SinglePayment',
    MultiplePayment = 'MultiplePayment',
    BiometricVerification = 'BiometricVerification',
    NpsSurvey = 'NpsSurvey',
    RequestAuthenticationCode = 'RequestAuthenticationCode',
    RequestUtr = 'RequestUtr',
    DataRequestSubmission = 'DataRequestSubmission',
    DataRequestPartiallySubmission = 'DataRequestPartiallySubmission',
    DataRequestUploadCsvOfBankStatements = 'DataRequestUploadCsvOfBankStatements',
    AccountantUploadFromSoftware = 'AccountantUploadFromSoftware',
    DataRequestDormantAccountTransactionConfirmation = 'DataRequestDormantAccountTransactionConfirmation',
    CompanySecretarial = 'CompanySecretarial',
    NewRaise = 'NewRaise',
    ChangeAssignee = 'ChangeAssignee',
    Resolved = 'Resolved',
    ReOpened = 'ReOpened',
    ReviewRequest = 'ReviewRequest',
    ShareLink = 'ShareLink',
    AddNotes = 'AddNotes',
    DirectDebitSetupConfirmationEmail = 'DirectDebitSetupConfirmationEmail',
    PaymentFailedCancelledNotification = 'PaymentFailedCancelledNotification',
    MandateCancelledExpiredFailedNotification = 'MandateCancelledExpiredFailedNotification',
    RefundFailureNotification = 'RefundFailureNotification',
    MemberHighRiskAlert = 'MemberHighRiskAlert',
    ClientHighRiskAlert = 'ClientHighRiskAlert',
    Sa100Submission = 'Sa100Submission',
    Sa100SaveForLater = 'Sa100SaveForLater',
    SecureLinkSaveForLater = 'SecureLinkSaveForLater',
    SecureLinkPartialSubmission = 'SecureLinkPartialSubmission',
    SecureLinkFinalSubmission = 'SecureLinkFinalSubmission',
    PayrollReviewRequest = 'PayrollReviewRequest',
    PayrollReviewResponse = 'PayrollReviewResponse',
    PayrollCalculationReviewRequest = 'PayrollCalculationReviewRequest',
    PayrollCalculationReviewResponse = 'PayrollCalculationReviewResponse',
    ReportIssue = 'ReportIssue',
    ResolveIssue = 'ResolveIssue'
}

export interface IEmailTemplateSubTypeOption {
    value: EmailTemplateSubType;
    label: string;
}

export const EmailTemplateSubTypeOptions: IEmailTemplateSubTypeOption[] = [
    // Standard
    { value: EmailTemplateSubType.ContactQuery, label: 'Contact query' },
    { value: EmailTemplateSubType.CallbackRequest, label: 'Callback request' },
    { value: EmailTemplateSubType.InternalCallbackRequest, label: 'Internal callback request' },
    { value: EmailTemplateSubType.QuickQuote, label: 'Quick quote' },
    { value: EmailTemplateSubType.QuoteViewed, label: 'Quote viewed' },
    { value: EmailTemplateSubType.QuoteAccepted, label: 'Quote accepted' },
    { value: EmailTemplateSubType.ServiceRequest, label: 'Service request' },
    { value: EmailTemplateSubType.Referral, label: 'Referral' },
    { value: EmailTemplateSubType.SinglePayment, label: 'Single payment' },
    { value: EmailTemplateSubType.MultiplePayment, label: 'Multiple payment' },
    { value: EmailTemplateSubType.BiometricVerification, label: 'Biometric verification' },
    { value: EmailTemplateSubType.NpsSurvey, label: 'NPS Survey' },

    // Data request
    { value: EmailTemplateSubType.RequestAuthenticationCode, label: 'Request Authentication code' },
    { value: EmailTemplateSubType.RequestUtr, label: 'Request UTR' },
    { value: EmailTemplateSubType.DataRequestSubmission, label: 'Data request submission' },
    { value: EmailTemplateSubType.DataRequestPartiallySubmission, label: 'Data request partially submission' },
    { value: EmailTemplateSubType.DataRequestUploadCsvOfBankStatements, label: 'Data request upload CSV of bank statements' },
    { value: EmailTemplateSubType.AccountantUploadFromSoftware, label: 'Accountant upload from software' },
    { value: EmailTemplateSubType.DataRequestDormantAccountTransactionConfirmation, label: 'Data request dormant account transaction confirmation' },
    { value: EmailTemplateSubType.CompanySecretarial, label: 'Company secretarial' },

    // Complaint
    { value: EmailTemplateSubType.NewRaise, label: 'New raise' },
    { value: EmailTemplateSubType.ChangeAssignee, label: 'Change assignee' },
    { value: EmailTemplateSubType.Resolved, label: 'Resolved' },
    { value: EmailTemplateSubType.ReOpened, label: 'Re-opened' },

    // Resource
    { value: EmailTemplateSubType.ReviewRequest, label: 'Review request' },
    { value: EmailTemplateSubType.ShareLink, label: 'Share link' },

    // Notes
    { value: EmailTemplateSubType.AddNotes, label: 'Add' },

    // Billing and finance
    { value: EmailTemplateSubType.DirectDebitSetupConfirmationEmail, label: 'Direct Debit setup confirmation email' },
    { value: EmailTemplateSubType.PaymentFailedCancelledNotification, label: 'Payment failed/cancelled notification' },
    { value: EmailTemplateSubType.MandateCancelledExpiredFailedNotification, label: 'Mandate cancelled/expired/failed notification' },
    { value: EmailTemplateSubType.RefundFailureNotification, label: 'Refund failure notification' },

    // AML
    { value: EmailTemplateSubType.MemberHighRiskAlert, label: 'Member - High risk alert' },
    { value: EmailTemplateSubType.ClientHighRiskAlert, label: 'Client - High risk alert' },

    // SA100 Data Request
    { value: EmailTemplateSubType.Sa100Submission, label: 'Submission' },
    { value: EmailTemplateSubType.Sa100SaveForLater, label: 'Save for later' },

    // Secure Link
    { value: EmailTemplateSubType.SecureLinkSaveForLater, label: 'Save for later' },
    { value: EmailTemplateSubType.SecureLinkPartialSubmission, label: 'Partial submission' },
    { value: EmailTemplateSubType.SecureLinkFinalSubmission, label: 'Final submission' },

    // Payroll Review
    { value: EmailTemplateSubType.PayrollReviewRequest, label: 'Payroll review request' },
    { value: EmailTemplateSubType.PayrollReviewResponse, label: 'Payroll review response' },

    // Payroll Calculation Review
    { value: EmailTemplateSubType.PayrollCalculationReviewRequest, label: 'Payroll calculation review request' },
    { value: EmailTemplateSubType.PayrollCalculationReviewResponse, label: 'Payroll calculation review response' },

    // Report
    { value: EmailTemplateSubType.ReportIssue, label: 'Report issue' },
    { value: EmailTemplateSubType.ResolveIssue, label: 'Resolve issue' }
];

export interface IDataConversionFile {
    name?: string;
    file?: IFileNameModel;
    type?: CsvTemplateType;
    subType?: string;
    emailTemplateSubType?: EmailTemplateSubType | string;
    rawFile?: File;
    validationStatus?: 'Not Uploaded' | 'Passed' | 'Failed' | string;
    errorCount?: number;
    validRowCount?: number;
    totalRowCount?: number;
    errorDetailsJson?: string;
    errorFileUrl?: string;
}

export interface IPracticeDataRequest {
    id?: string;
    clientEmail?: string;
    token?: string;
    referenceNumber?: string;
    practiceName?: string;
    logoUrl?: string | IFileNameModel;
    faviconUrl?: string | IFileNameModel;
    website?: string;
    practicePhoneNumber?: string;
    invoiceSampleHeaderUrl?: string | IFileNameModel;
    invoiceSampleFooterUrl?: string | IFileNameModel;
    publicEmailNoReply?: string;
    proposedUrl?: string;
    portalUrlOption?: PortalUrlType | number;
    customPortalUrl?: string;
    customNoReplyEmail?: string;
    emailProvider?: EmailProviderType | number;
    userCreationMethod?: UserCreationMethod | number;
    preferredLoginMode?: LoginMode | number;
    contactPersonName?: string;
    contactPersonEmail?: string;
    contactPersonPhone?: string;
    dataConversionFiles?: IDataConversionFile[];
    currentStep?: number;
    activeSubTab?: string;
    status?: PracticeDataRequestStatus | number;
    requestType?: PracticeDataRequestType | number;
}

export const CsvTemplateOptions = [
    { value: CsvTemplateType.Businesses, label: 'Businesses Import' },
    { value: CsvTemplateType.Contacts, label: 'Contacts Import' },
    { value: CsvTemplateType.CreditNotes, label: 'Credit Notes Import' },
    { value: CsvTemplateType.Invoices, label: 'Invoices Import' },
    { value: CsvTemplateType.Subscriptions, label: 'Subscriptions and DD Import' },
    { value: CsvTemplateType.Receipts, label: 'Receipts Import' },
    { value: CsvTemplateType.Tasks, label: 'Tasks Import' },
    { value: CsvTemplateType.Users, label: 'Users Import' },
    { value: CsvTemplateType.ServicesAndPackages, label: 'Services and Packages' },
    { value: CsvTemplateType.EmailTemplates, label: 'Email Templates' },
    { value: CsvTemplateType.ESignatureContent, label: 'E-Signature Content' },
    { value: CsvTemplateType.LoeContent, label: 'LOE (Letter of Engagement)' },
    // { value: CsvTemplateType.PracticeProfileQuestionnaire, label: 'Practice Profile - Questionnaire' },
    { value: CsvTemplateType.BankAccountsForCollection, label: 'Bank Accounts for Collection' },
    // { value: CsvTemplateType.Credentials, label: 'Credentials' },
    { value: CsvTemplateType.KycDocumentsList, label: 'KYC Documents List' },
    // { value: CsvTemplateType.AutomationAndEmailReminder, label: 'Automation & Email Reminders' },
    { value: CsvTemplateType.TeamsTemplate, label: 'Teams Template' },
    { value: CsvTemplateType.Other, label: 'Other Data Import' }
];

export const NUMBER_TO_ENUM: Record<number, CsvTemplateType> = {
    0: CsvTemplateType.Unknown,
    1: CsvTemplateType.Other,
    2: CsvTemplateType.Contacts,
    3: CsvTemplateType.Users,
    4: CsvTemplateType.Receipts,
    5: CsvTemplateType.Businesses,
    6: CsvTemplateType.CreditNotes,
    7: CsvTemplateType.Tasks,
    8: CsvTemplateType.Subscriptions,
    9: CsvTemplateType.Invoices,
    10: CsvTemplateType.ServicesAndPackages,
    11: CsvTemplateType.EmailTemplates,
    12: CsvTemplateType.ESignatureContent,
    13: CsvTemplateType.LoeContent,
    14: CsvTemplateType.PracticeProfileQuestionnaire,
    15: CsvTemplateType.BankAccountsForCollection,
    16: CsvTemplateType.Credentials,
    17: CsvTemplateType.KycDocumentsList,
    18: CsvTemplateType.AutomationAndEmailReminder,
    19: CsvTemplateType.TeamsTemplate
};

export const getEnumTypeValue = (val: any): CsvTemplateType => {
    if (val === null || val === undefined) return CsvTemplateType.Other;

    const num = Number(val);
    if (!isNaN(num) && NUMBER_TO_ENUM[num] !== undefined) {
        return NUMBER_TO_ENUM[num];
    }

    const strVal = String(val).trim().toLowerCase();
    const opt = CsvTemplateOptions.find(
        (o) => o.value.toLowerCase() === strVal || o.label.toLowerCase() === strVal
    );
    if (opt) return opt.value;

    if (strVal === 'other') return CsvTemplateType.Other;
    if (strVal === 'unknown') return CsvTemplateType.Unknown;

    return CsvTemplateType.Other;
};

class PracticeDataRequestService {
    route = `/api/PracticeDataRequests`;

    getPracticeDataRequests = (start: number, length: number, search: string, requestType?: PracticeDataRequestType) => {
        return ApiUtility.get(`${this.route}/List`, { start, length, search, requestType });
    };

    getPracticeDataRequestById = (id: string) => {
        return ApiUtility.get(`${this.route}/${id}`);
    };

    updatePracticeDataRequest = (data: any) => {
        if (data instanceof FormData) {
            return ApiUtility.postForm(`${this.route}/Update`, data);
        }
        return ApiUtility.post(`${this.route}/Update`, data);
    };

    addPracticeDataRequest = (data: any) => {
        return ApiUtility.post(`${this.route}`, data);
    };

    generateLink = (data: { clientEmail?: string; practiceName?: string; requestType?: PracticeDataRequestType }) => {
        return ApiUtility.post(`${this.route}/GenerateLink`, data);
    };

    resendEmail = (id: string) => {
        return ApiUtility.post(`${this.route}/ResendEmail/${id}`, {});
    };

    getPublicRequest = (token: string) => {
        return ApiUtility.get(`${this.route}/Public/${token}`, { withCredentials: true });
    };

    publicSubmit = (token: string, data: any, stepType?: string) => {
        const url = stepType ? `${this.route}/PublicSubmit/${token}?stepType=${stepType}` : `${this.route}/PublicSubmit/${token}`;
        if (data instanceof FormData) {
            return ApiUtility.postForm(url, data);
        }
        return ApiUtility.post(url, data);
    };

    getPublicRequestEmail = (token: string) => {
        return ApiUtility.get(`${this.route}/PublicEmail/${token}`);
    };

    generateOtp = (email: string) => {
        return ApiUtility.post(`/api/Auth/${encodeURIComponent(email)}/GenerateOTP`, {});
    };

    validateOtp = (data: { email: string; otp: string }) => {
        return ApiUtility.post(`/api/Auth/ValidateOTP`, data);
    };

    getEmailTemplateSubTypes = async (): Promise<{ value: string; label: string }[]> => {
        try {
            const response = await ApiUtility.get(`${this.route}/EmailTemplateSubTypes`);
            if (response?.status && Array.isArray(response?.result)) {
                return response.result as { value: string; label: string }[];
            }
        } catch (e) {
            console.warn('Failed to fetch email template subtypes from API, using fallback.', e);
        }
        // Fallback to the static list if the API call fails
        return EmailTemplateSubTypeOptions.map(o => ({ value: String(o.value), label: o.label }));
    };
    
    // Admin asset endpoints require a Bearer token, which a plain <img src> can't attach,
    // so assets must be fetched as an authenticated blob and turned into an object URL.
    getAssetBlobUrl = async (id: string, assetKey: string): Promise<string | null> => {
        try {
            const response = await ApiUtility.getResponse(
                `${this.route}/${id}/Asset/${assetKey}`,
                undefined,
                { responseType: 'blob' } as any,
            );
            const contentType = (response.headers && response.headers['content-type']) || 'image/png';
            const blob = new Blob([response.data], { type: contentType as any });
            return URL.createObjectURL(blob);
        } catch (error) {
            console.error(`Error fetching asset ${assetKey}:`, error);
            return null;
        }
    };

    // Same reasoning as getAssetBlobUrl: DownloadFile is [Authorize]-protected, so it must go
    // through ApiUtility (Bearer header) rather than a plain <a href>, then trigger a save-as.
    downloadDataConversionFile = async (id: string, fileId: string, fallbackName?: string) => {
        try {
            const response = await ApiUtility.getResponse(
                `${this.route}/${id}/DownloadFile/${encodeURIComponent(fileId)}`,
                undefined,
                { responseType: 'blob' } as any,
            );

            let filename = fallbackName || 'download.csv';
            const contentDisposition = response.headers && response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(contentDisposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/['"]/g, '');
                }
            }

            const contentType = (response.headers && response.headers['content-type']) || 'application/octet-stream';
            const blob = new Blob([response.data], { type: contentType as any });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error(`Error downloading file ${fileId}:`, error);
        }
    };

    downloadCsvTemplate = async (type: CsvTemplateType) => {
        try {
            const response = await ApiUtility.getResponse(`${this.route}/CsvTemplate`, { type }, { responseType: 'blob' } as any);
            
            let filename = `${type}Template.csv`;
            const contentDisposition = response.headers && response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(contentDisposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/['"]/g, '');
                }
            }
            
            const contentType = (response.headers && response.headers['content-type']) || 'text/csv;charset=utf-8;';
            const blob = new Blob([response.data], { type: contentType as any });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading CSV template:", error);
        }
    };
}

export default new PracticeDataRequestService();
